import React, { useEffect } from "react"
import { AlertMessage, TeamRoundPlayer } from "../../types"

interface KeyboardProps {
  player: TeamRoundPlayer
  max: number
  score: number | null
  savedScore: number | null
  updateScore: (player: TeamRoundPlayer, score: number | null) => void
  dismiss()
  showAlert: (message: AlertMessage) => void
}

function digitFromKeyboardEvent(event: KeyboardEvent): number | null {
  if (event.key >= "1" && event.key <= "9") {
    return parseInt(event.key, 10)
  }

  const numpadMatch = event.code.match(/^Numpad([1-9])$/)
  if (numpadMatch) {
    return parseInt(numpadMatch[1], 10)
  }

  return null
}

export default function Keyboard({ player, max, score, savedScore, updateScore, dismiss, showAlert }: KeyboardProps) {
  const updateScoreForPlayer = (newScore: number | null) => {
    if (newScore > max) {
      showAlert({
        title: "Invalid Score",
        message: `Max score for ${player.user.firstName} on this hole is ${max}`,
        buttons: [{ text: "Ok", style: "primary" }]
      })
    } else {
      updateScore(player, newScore)
    }
  }
  const clearScore = () => {
    updateScore(player, null)
  }
  const deleteScore = () => {
    updateScore(player, 0)
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Leave browser shortcuts like Cmd+1 (tab switching) alone.
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return
      }

      if (event.key === "Escape" || event.key === "Enter") {
        event.preventDefault()
        dismiss()
        return
      }

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault()
        updateScore(player, null)
        return
      }

      const digit = digitFromKeyboardEvent(event)
      if (digit == null) {
        return
      }

      event.preventDefault()
      updateScoreForPlayer(digit)
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [dismiss, max, player, showAlert, updateScore])

  return (
    <div className="round-scoring-keyboard">
      <div className="round-scoring-keyboard-container">
        <div className="round-scoring-keyboard-header">
          {savedScore ? (
            <div onClick={deleteScore}>Delete</div>
          ) : score ? (
            <div onClick={clearScore}>Clear</div>
          ) : (
            <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
          )}
          <div className="round-scoring-keyboard-player">{player.user.firstName}'s Score</div>
          <div className="round-scoring-keyboard-done" onClick={dismiss}>
            Done
          </div>
        </div>
        <div className="round-scoring-keyboard-keys">
          <Key number={1} max={max} score={score} updateScore={updateScoreForPlayer} />
          <Key number={2} max={max} score={score} updateScore={updateScoreForPlayer} />
          <Key number={3} max={max} score={score} updateScore={updateScoreForPlayer} />
        </div>
        <div className="round-scoring-keyboard-keys">
          <Key number={4} max={max} score={score} updateScore={updateScoreForPlayer} />
          <Key number={5} max={max} score={score} updateScore={updateScoreForPlayer} />
          <Key number={6} max={max} score={score} updateScore={updateScoreForPlayer} />
        </div>
        <div className="round-scoring-keyboard-keys">
          <Key number={7} max={max} score={score} updateScore={updateScoreForPlayer} />
          <Key number={8} max={max} score={score} updateScore={updateScoreForPlayer} />
          <Key number={9} max={max} score={score} updateScore={updateScoreForPlayer} />
        </div>
      </div>
    </div>
  )
}

interface KeyProps {
  number: number
  max: number
  score: number | null
  updateScore: (score: number | null) => void
}

function Key({ number, max, score, updateScore }: KeyProps) {
  const selected = score === number
  return (
    <div
      onClick={() => updateScore(number)}
      className={`round-scoring-keyboard-key ${number > max ? "disabled" : ""} ${selected ? "selected" : ""}`}
    >
      {number}
    </div>
  )
}
